import { effect, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, Routes } from '@angular/router';
import { filter } from 'rxjs/operators';
import { authGuard } from '../guard/auth.guard';
import { AuthService } from './auth.service';

/**
 * Reactive backstop for the route guards. `authGuard` only runs on navigation,
 * so a session that ends while the user sits on a protected page (token expiry,
 * a logout whose redirect was cancelled, another tab logging out) would
 * otherwise leave them stranded - authenticated-looking UI with no token. This
 * service watches the auth status and, when it goes anonymous on a protected
 * route, sends the user to /login.
 *
 * "Protected" is derived from the route config (routes carrying `authGuard`)
 * rather than a public-route allowlist, so a new public route can never be
 * accidentally caught (e.g. the unguarded /app-reauth stale-cookie bounce).
 */
@Injectable({ providedIn: 'root' })
export class SessionRedirectService {
    private readonly auth = inject(AuthService);
    private readonly router = inject(Router);
    private readonly protectedPaths = this.collectAuthGuardedPaths(this.router.config);

    constructor() {
        // The session ended. Defer to a macrotask so an intentional
        // logout-then-navigate flow (e.g. to /verify or /try) settles on its
        // public destination first; if it does, the check below no-ops.
        effect(() => {
            if (this.auth.state().status === 'anonymous') {
                setTimeout(() => this.redirectIfStranded());
            }
        });

        // A navigation settled. Re-check, so a same-URL /chat navigation that
        // lands back on a protected route while anonymous (and was skipped by
        // the in-flight guard below) is caught instead of stranding the user.
        // Deferred so the router has fully cleared the just-finished navigation.
        this.router.events
            .pipe(
                filter(e => e instanceof NavigationEnd),
                takeUntilDestroyed()
            )
            .subscribe(() => setTimeout(() => this.redirectIfStranded()));
    }

    redirectIfStranded(): void {
        if (this.auth.state().status !== 'anonymous') {
            return;
        }
        // A navigation is still under way - e.g. an intentional
        // logout({ redirect: false }) followed by a router.navigate() to /try or
        // /verify whose lazy chunk hasn't landed yet. Don't act mid-flight; the
        // NavigationEnd handler re-checks once it settles, so this is never a
        // permanent skip.
        if (this.router.getCurrentNavigation()) {
            return;
        }
        if (!this.isOnProtectedRoute()) {
            return;
        }
        void this.auth.redirectToLogin();
    }

    private isOnProtectedRoute(): boolean {
        const path = (this.router.url.split('?')[0] ?? '').split('#')[0];
        return this.protectedPaths.some(p => path === p || path.startsWith(`${p}/`));
    }

    /** Walk the route config and collect every path guarded by `authGuard`. */
    private collectAuthGuardedPaths(routes: Routes | undefined, parent = ''): string[] {
        const out: string[] = [];
        for (const route of routes ?? []) {
            const full = [parent, route.path ?? ''].filter(Boolean).join('/');
            if (route.canActivate?.includes(authGuard)) {
                out.push(`/${full}`);
            }
            if (route.children) {
                out.push(...this.collectAuthGuardedPaths(route.children, full));
            }
        }
        return out;
    }
}
